import { useSyncExternalStore } from "react";

/**
 * Data Privacy Mode: a Paisley admin turns it on for this browser tab before a demo, and the
 * Choice dashboard, By School and By Program show fake names, IDs, addresses and schools on
 * screen and in downloads (see dataPrivacyMasker). Masking happens in the browser: a demo aid,
 * not a security control. Kept in sessionStorage, so it survives a reload but ends with the tab.
 */
const STORAGE_KEY = "paisley.dataPrivacy";
/** Session keys of the masker (its salt and school registry), cleared when the mode is switched off. */
export const DATA_PRIVACY_SESSION_KEYS = ["paisley.dataPrivacy.salt", "paisley.dataPrivacy.schools"];
/** Pages whose data the backend masks; elsewhere the banner says it is not applied. */
export const DATA_PRIVACY_PAGES = ["/choiceDashboard", "/schoolSeats", "/programSeats"];
export const DEMO_ORGANIZATION_NAME = "Demo District";

const listeners = new Set<() => void>();

const read = (): boolean => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

let current = read();

export const isDataPrivacyOn = (): boolean => current;

export const setDataPrivacy = (on: boolean): void => {
  current = on;
  try {
    if (on) window.sessionStorage.setItem(STORAGE_KEY, "1");
    else {
      window.sessionStorage.removeItem(STORAGE_KEY);
      DATA_PRIVACY_SESSION_KEYS.forEach((k) => window.sessionStorage.removeItem(k));
    }
  } catch {
    // Storage blocked: the mode still applies until the page reloads.
  }
  listeners.forEach((l) => l());
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export const useDataPrivacy = (): boolean => useSyncExternalStore(subscribe, isDataPrivacyOn, isDataPrivacyOn);

/**
 * Switches the mode and reloads the current page without its query: page state (filters, search,
 * the Choice URL) holds real or masked names, and must not carry across the switch.
 */
export const toggleDataPrivacy = (): void => {
  setDataPrivacy(!current);
  window.location.assign(window.location.pathname);
};
